/**
 * Plays a film into a canvas on the page: a clock, a loop, and a canvas kept at the
 * size it is shown at.
 *
 * The film keeps no state of its own, so playing is only moving `t` and drawing; seeking
 * is setting it. The clock advances by the time between frames, never by more than a
 * tenth of a second at once, so a tab brought back from the background picks up where
 * it was rather than jumping to the end.
 */

import { renderFrame, type Film, type Site } from "./film.js";
import type { Palette } from "./palette.js";

/** The sharpest a canvas is drawn: past twice the CSS size there is nothing more to see. */
const MAX_DPR = 2;

/** A frame that takes longer than this to draw is a device asking for fewer pixels. */
const SLOW_FRAME_MS = 24;

export class Player {
  private t = 0;
  private playing = false;
  private frame = 0;
  private last = 0;
  /** How much of the device's pixel ratio the canvas is drawn at: lowered on a slow device. */
  private sharpness = 1;
  private slowFrames = 0;
  /** A clock to follow instead of the frame loop's own: the soundtrack's, while it plays. */
  private clock: (() => number | undefined) | undefined;
  private readonly listeners = new Set<() => void>();
  private readonly resize: ResizeObserver;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    readonly film: Film,
    private readonly palette: Palette,
    private readonly site: Site,
  ) {
    this.resize = new ResizeObserver(() => this.draw());
    this.resize.observe(canvas);
    this.draw();
  }

  get time(): number {
    return this.t;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get ended(): boolean {
    return this.t >= this.film.duration;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  play(): void {
    if (this.playing) {
      return;
    }
    if (this.ended) {
      this.t = 0;
    }
    this.playing = true;
    this.last = performance.now();
    this.frame = requestAnimationFrame(this.tick);
    this.notify();
  }

  pause(): void {
    if (!this.playing) {
      return;
    }
    this.playing = false;
    cancelAnimationFrame(this.frame);
    this.notify();
  }

  toggle(): void {
    if (this.playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  seek(t: number): void {
    this.t = Math.min(this.film.duration, Math.max(0, t));
    this.draw();
    this.notify();
  }

  /**
   * Follows `clock` while it has a time to give, so the picture keeps to the sound rather
   * than the two drifting apart; `undefined` goes back to counting frames.
   */
  follow(clock: (() => number | undefined) | undefined): void {
    this.clock = clock;
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.resize.disconnect();
    this.listeners.clear();
  }

  private readonly tick = (now: number): void => {
    const step = Math.min(0.1, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    const heard = this.clock?.();
    this.t = Math.min(this.film.duration, heard ?? this.t + step);
    if (this.ended) {
      this.playing = false;
    }
    this.draw();
    this.notify();
    if (this.playing) {
      this.frame = requestAnimationFrame(this.tick);
    }
  };

  private draw(): void {
    const { canvas } = this;
    const ratio = Math.max(0.75, Math.min(MAX_DPR, window.devicePixelRatio || 1) * this.sharpness);
    const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
    const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
      return;
    }
    const started = performance.now();
    renderFrame(ctx, this.film, this.t, width, height, this.palette, this.site);
    // A few slow frames in a row while playing, and the next ones are drawn smaller and
    // scaled up by the browser: a softer film rather than a stuttering one.
    if (this.playing && performance.now() - started > SLOW_FRAME_MS) {
      this.slowFrames += 1;
      if (this.slowFrames >= 8 && this.sharpness > 0.5) {
        this.sharpness -= 0.15;
        this.slowFrames = 0;
      }
    } else {
      this.slowFrames = 0;
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
